import { Button, Form, Input, InputNumber, Modal, Progress, Radio, Select, Table, Upload } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';

const UploadContentModal = ({
  open,
  onOk,
  onCancel,
  confirmLoading,
  uploadForm,
  user,
  fileList,
  uploadItems,
  selectedUploadUid,
  selectedUploadItem,
  uploadItemForm,
  batchGenerating,
  previewing,
  previewingAction,
  globalTextSource,
  setGlobalTextSource,
  globalOcrMaxFrames,
  setGlobalOcrMaxFrames,
  batchSkipDone,
  setBatchSkipDone,
  batchProgress,
  batchFailures,
  handleFileChange,
  handleRemove,
  selectUploadItem,
  syncSelectedItemFromForm,
  onUploadItemValuesChange,
  doPreviewForSelected,
  applyGlobalTextSourceToUntouched,
  runBatchGenerate,
  contentDeliveryPlatformOptions,
}) => (
  <Modal
    title="上传内容"
    open={open}
    onOk={onOk}
    onCancel={onCancel}
    confirmLoading={confirmLoading}
    okText="上传"
    cancelText="取消"
    width={900}
  >
    <Form
      form={uploadForm}
      layout="vertical"
      initialValues={{
        source: '自建',
        status: '待发布',
      }}
    >
      <Form.Item label="创作者">
        <Input value={user?.nickname || user?.name || user?.user_id || '-'} disabled />
      </Form.Item>

      <Form.Item
        label="选择文件"
        required
        rules={[
          {
            validator: () => {
              if (fileList.length === 0) {
                return Promise.reject(new Error('请至少选择一个文件'));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Upload
          multiple
          fileList={fileList}
          onChange={handleFileChange}
          onRemove={handleRemove}
          beforeUpload={() => false}
          accept=".mp4,.avi,.mov,.wmv,.flv,.mkv,.doc,.docx,.pdf,.jpg,.jpeg,.png,.gif"
        >
          <Button icon={<CloudUploadOutlined />}>选择文件</Button>
          <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
            支持视频（.mp4等）、图文（Word文件）、笔记（图片+文档）
          </div>
        </Upload>
      </Form.Item>

      {uploadItems.length > 0 && (
        <div style={{ display: 'flex', gap: 12, border: '1px solid #f0f0f0', padding: 12, borderRadius: 8, marginBottom: 12 }}>
          <div style={{ width: 220 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>文件列表</div>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {(uploadItems || []).map((it) => {
                const active = it.uid === selectedUploadUid;
                const statusText = it.aiStatus === 'loading' ? '生成中' : it.aiStatus === 'done' ? '已生成' : it.aiStatus === 'error' ? '生成失败' : '未生成';
                const statusColor = it.aiStatus === 'done' ? '#52c41a' : it.aiStatus === 'error' ? '#ff4d4f' : it.aiStatus === 'loading' ? '#1677ff' : '#999';
                return (
                  <div
                    key={it.uid}
                    onClick={() => selectUploadItem(it.uid)}
                    style={{
                      cursor: 'pointer',
                      padding: '8px 10px',
                      borderRadius: 6,
                      border: active ? '1px solid #1677ff' : '1px solid #f0f0f0',
                      background: active ? '#e6f4ff' : '#fff',
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, wordBreak: 'break-all' }}>{it.name}</div>
                    <div style={{ fontSize: 12, color: statusColor }}>{statusText}</div>
                    {it.aiStatus === 'error' && it.aiError ? (
                      <div style={{ fontSize: 12, color: '#ff4d4f', wordBreak: 'break-all' }}>{it.aiError}</div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>当前文件元数据</div>
            {!selectedUploadItem ? (
              <div style={{ color: '#999' }}>请选择一个文件</div>
            ) : (
              <Form
                form={uploadItemForm}
                layout="vertical"
                onValuesChange={(changed) => {
                  onUploadItemValuesChange(changed);
                  syncSelectedItemFromForm();
                }}
              >
                <Form.Item label="AI文本来源" name="text_source" initialValue="asr">
                  <Radio.Group
                    disabled={batchGenerating}
                    options={[
                      { label: 'ASR（语音）', value: 'asr' },
                      { label: 'OCR（抽帧）', value: 'image' },
                      { label: '自动', value: 'auto' },
                    ]}
                    optionType="button"
                    buttonStyle="solid"
                  />
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevVals, curVals) => prevVals?.text_source !== curVals?.text_source}
                >
                  {({ getFieldValue }) => {
                    const ts = String(getFieldValue('text_source') || 'asr');
                    if (ts === 'asr') return null;
                    return (
                      <Form.Item
                        label="OCR抽帧数"
                        name="ocr_max_frames"
                        extra="仅在 OCR/自动 模式下生效；帧数越多越慢。"
                      >
                        <InputNumber min={1} max={200} style={{ width: 220 }} disabled={batchGenerating} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
                <Form.Item label="标题" name="title">
                  <Input
                    disabled={batchGenerating}
                    placeholder="标题（默认文件名）"
                    addonAfter={(
                      <Button
                        type="link"
                        size="small"
                        loading={previewing && previewingAction === 'title'}
                        disabled={batchGenerating || (previewing && previewingAction !== 'title')}
                        onClick={(e) => {
                          e.preventDefault();
                          doPreviewForSelected('title');
                        }}
                      >
                        AI生成
                      </Button>
                    )}
                  />
                </Form.Item>
                <Form.Item
                  label="内容简介"
                  name="summary"
                  extra="建议先点击 AI生成；如不合适可直接修改。"
                >
                  <Input.TextArea
                    disabled={batchGenerating}
                    rows={4}
                    placeholder="点击右侧 AI生成 生成内容简介"
                  />
                </Form.Item>
                <Form.Item label="标签" name="tags">
                  <Select
                    mode="tags"
                    disabled={batchGenerating}
                    placeholder="回车添加标签"
                    style={{ width: '100%' }}
                    onBlur={() => syncSelectedItemFromForm()}
                  />
                </Form.Item>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <Button
                    type="primary"
                    loading={previewing && previewingAction === 'all'}
                    disabled={batchGenerating || (previewing && previewingAction !== 'all')}
                    onClick={() => doPreviewForSelected('all')}
                  >
                    AI生成简介/标签
                  </Button>
                </div>
              </Form>
            )}
          </div>
        </div>
      )}

      {uploadItems.length >= 2 && (
        <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>批量辅助</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 360 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>全局 AI 文本来源（仅应用到未修改的文件）</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Radio.Group
                  value={globalTextSource}
                  onChange={(e) => setGlobalTextSource(e?.target?.value)}
                  optionType="button"
                  buttonStyle="solid"
                  disabled={batchGenerating || previewing}
                  options={[
                    { label: 'ASR（语音）', value: 'asr' },
                    { label: 'OCR（抽帧）', value: 'image' },
                    { label: '自动', value: 'auto' },
                  ]}
                />
                {(globalTextSource === 'image' || globalTextSource === 'auto') && (
                  <InputNumber
                    min={1}
                    max={200}
                    value={globalOcrMaxFrames}
                    onChange={(v) => setGlobalOcrMaxFrames(Number(v || 6))}
                    style={{ width: 140 }}
                    disabled={batchGenerating || previewing}
                  />
                )}
                <Button onClick={applyGlobalTextSourceToUntouched} disabled={batchGenerating || previewing}>
                  应用到未修改文件
                </Button>
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 360 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>一键为所有内容生成 标题/简介/标签（并发=3）</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Select
                  value={batchSkipDone ? 'skip' : 'all'}
                  onChange={(v) => setBatchSkipDone(v === 'skip')}
                  style={{ width: 180 }}
                  disabled={batchGenerating || previewing}
                  options={[
                    { label: '跳过已生成', value: 'skip' },
                    { label: '包含已生成', value: 'all' },
                  ]}
                />
                <Button
                  type="primary"
                  loading={batchGenerating}
                  disabled={previewing}
                  onClick={() => runBatchGenerate({ retryOnlyFailures: false })}
                >
                  一键生成全部
                </Button>
                <Button
                  disabled={batchGenerating || previewing || (batchFailures || []).length === 0}
                  onClick={() => runBatchGenerate({ retryOnlyFailures: true })}
                >
                  失败重试
                </Button>
              </div>

              {batchProgress.total > 0 && (
                <div style={{ marginTop: 10 }}>
                  <Progress
                    percent={Math.round((batchProgress.done / Math.max(batchProgress.total, 1)) * 100)}
                    status={batchGenerating ? 'active' : (batchProgress.failed > 0 ? 'exception' : 'success')}
                  />
                  <div style={{ fontSize: 12, color: '#666' }}>
                    进度：{batchProgress.done}/{batchProgress.total}；成功：{batchProgress.ok}；失败：{batchProgress.failed}
                  </div>
                </div>
              )}

              {(batchFailures || []).length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>失败列表</div>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={(batchFailures || []).map((it, idx) => ({ key: it.uid || String(idx), ...it }))}
                    columns={[
                      { title: '文件', dataIndex: 'name', key: 'name', width: 220, render: (v) => <div style={{ wordBreak: 'break-all' }}>{v}</div> },
                      { title: '原因', dataIndex: 'error', key: 'error', render: (v) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{String(v || '')}</div> },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Form.Item label="内容来源" name="source">
        <Select
          style={{ width: '100%' }}
          options={[
            { label: '自建', value: '自建' },
            { label: '抓取', value: '抓取' },
            { label: 'AI生成', value: 'AI生成' },
          ]}
        />
      </Form.Item>

      <Form.Item label="投递平台" name="available_platforms">
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="请选择投递平台（可选）"
          options={contentDeliveryPlatformOptions}
        />
      </Form.Item>

      <Form.Item label="内容状态" name="status">
        <Select
          style={{ width: '100%' }}
          options={[
            { label: '待发布', value: '待发布' },
            { label: '已发布', value: '已发布' },
            { label: '草稿', value: '草稿' },
            { label: '已下线', value: '已下线' },
            { label: '作废', value: '作废' },
          ]}
        />
      </Form.Item>
    </Form>
  </Modal>
);

export default UploadContentModal;
